import { TableName } from '@openx/components-table/Table';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import type {
  DealInsightsChartInputApi,
  DealInsightsCreateJobIdParamsApi,
  DealInsightsFilterOptions,
} from '@openx/types/dealInsights';
import type { AllTableCriteria, CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import isEqual from 'lodash/isEqual';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { filtersToApiParams } from '../../utils/dealInsightsApiUtils';
import { useDealInsights } from '../../utils/hooks/dealInsights/useDealInsights';
import type { DateResolution } from '../components/DateResolutionToggle/DateResolutionToggle';
import { DEAL_INSIGHTS_PATHS, dealInsightsCountries, dealInsightsMetrics } from '../constants';
import { useDealInsigthsInitialState } from '../hooks/useDealInsigthsInitialState';
import { useDefaultCriteria } from '../hooks/useDefaultCriteria';
import type {
  DealInsightsContextType,
  DealInsightsCountry,
  DealInsightsDateRange,
  DealInsightsMetric,
  DealInsightsState,
} from '../types';
import { getDealInsightsSupportedDateRange } from '../utils/getDealInsightsSupportedDateRange';
import { getTableConfig } from '../utils/getTableConfig';
import { getActiveTab } from '../utils/tabUtils';

export const DealInsightsContext = createContext<DealInsightsContextType | undefined>(undefined);

type DealInsightsProviderProps = {
  children: ReactNode;
  /** Replaces the date range and filters derived from the URL, e.g. when seeded by the new Deal Insights page. */
  initialState?: Pick<DealInsightsState, 'filters' | 'dateRange'>;
};

export const DealInsightsProvider = ({ children, initialState: initialStateOverride }: DealInsightsProviderProps) => {
  const defaultInitialState = useDealInsigthsInitialState();
  const initialState = initialStateOverride ?? defaultInitialState;
  const { maxDate } = getDealInsightsSupportedDateRange();

  const [dateRange, setDateRange] = useState<DealInsightsDateRange>(initialState.dateRange);
  const [filters, setFilters] = useState<DealInsightsFilterOptions>(initialState.filters);

  const [country, setCountry] = useState<DealInsightsCountry>(dealInsightsCountries.UNITED_STATES);
  const [metric, setMetric] = useState<DealInsightsMetric>(dealInsightsMetrics.IMPRESSIONS);

  // Track date resolutions for different charts
  const [chartDateResolutions, setChartDateResolutions] = useState<{
    spend: DateResolution;
    bidrate_winrate: DateResolution;
  }>({
    spend: 'daily',
    bidrate_winrate: 'daily',
  });

  // Function to update date resolution for a specific chart
  const updateChartDateResolution = useCallback(
    (chartType: 'spend' | 'bidrate_winrate', resolution: DateResolution) => {
      setChartDateResolutions(prev => ({
        ...prev,
        [chartType]: resolution,
      }));
    },
    []
  );

  const location = useLocation();
  const activeTab = getActiveTab(location.pathname);

  const { useAccountUid } = useTargetingContext();
  const accountUid = useAccountUid();

  const defaultCriteria = useDefaultCriteria();
  const [criteria, setCriteria] = useState<Record<string, AllTableCriteria>>(defaultCriteria);

  useEffect(() => {
    const tableName = TableName.DEAL_INSIGHTS_GENERAL;
    const defaultGeneralSort = defaultCriteria[tableName]?.sort;
    const currentGeneralSort = criteria[tableName]?.sort;

    if (!defaultGeneralSort || !currentGeneralSort) {
      return;
    }

    if (isEqual(defaultGeneralSort.sortableColumns, currentGeneralSort.sortableColumns)) {
      return;
    }

    setCriteria(prev => {
      const prevGeneralCriteria = prev[tableName];
      const prevGeneralSort = prevGeneralCriteria?.sort;

      if (!prevGeneralCriteria || !prevGeneralSort) {
        return prev;
      }

      const nextSortableColumns = defaultGeneralSort.sortableColumns;
      const shouldFallbackSortColumn = !nextSortableColumns.includes(prevGeneralSort.column);

      return {
        ...prev,
        [tableName]: {
          ...prevGeneralCriteria,
          sort: {
            ...prevGeneralSort,
            sortableColumns: nextSortableColumns,
            column: shouldFallbackSortColumn ? defaultGeneralSort.column : prevGeneralSort.column,
            direction: shouldFallbackSortColumn ? defaultGeneralSort.direction : prevGeneralSort.direction,
          },
        },
      };
    });
  }, [defaultCriteria, criteria]);

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension>, tableName: TableName) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria[tableName], newCriteria);
      if (!isEqual(newCriteriaProcessed, criteria[tableName])) {
        setCriteria(prev => ({ ...prev, [tableName]: newCriteriaProcessed }));
      }
    },
    [criteria]
  );

  const chartInputsConfig: DealInsightsChartInputApi = useMemo(() => {
    switch (activeTab.path) {
      case DEAL_INSIGHTS_PATHS.delivery:
        return {
          delivery_by_domain_bundle: getTableConfig(
            TableName.DEAL_INSIGHTS_DELIVERY_BY_DOMAIN_BUNDLE,
            criteria,
            'impressions'
          ),
          delivery_by_ad_size: getTableConfig(TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_SIZE, criteria, 'impressions'),
          audiences: getTableConfig(TableName.DEAL_INSIGHTS_AUDIENCES, criteria, 'impressions'),
          delivery_by_ad_duration: true,
        };
      case DEAL_INSIGHTS_PATHS.distribution:
        return {
          inventory_distributions: true,
        };
      case DEAL_INSIGHTS_PATHS.geolocation:
        return {
          geolocation: true,
        };
      default:
        return {
          general_stats: true,
          spend: { include: true, date_resolution: chartDateResolutions.spend },
          bidrate_winrate: { include: true, date_resolution: chartDateResolutions.bidrate_winrate },
          deal_stats: getTableConfig(TableName.DEAL_INSIGHTS_GENERAL, criteria, 'impressions'),
        };
    }
  }, [activeTab, criteria, chartDateResolutions]);

  const params: DealInsightsCreateJobIdParamsApi = useMemo(
    () => ({
      ...filtersToApiParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate ?? maxDate,
        account: accountUid,
        ...filters,
      }),
      charts: chartInputsConfig,
    }),
    [dateRange, filters, chartInputsConfig, accountUid, maxDate]
  );

  const updateTotalCount = useCallback((tableCounts: Partial<Record<TableName, number>>) => {
    setCriteria(prev => {
      const newCriteria = { ...prev };
      Object.entries(tableCounts).forEach(([tableName, totalCount]) => {
        const pagination = newCriteria[tableName]?.pagination;
        if (pagination) {
          newCriteria[tableName] = {
            ...newCriteria[tableName],
            pagination: { ...pagination, totalCount },
          };
        }
      });
      return newCriteria;
    });
  }, []);

  const dealInsights = useDealInsights({ ...params, activeTab });

  useEffect(() => {
    if (dealInsights.data) {
      const tableCounts = {
        [TableName.DEAL_INSIGHTS_GENERAL]: dealInsights.data.deal_stats?.total_rows,
        [TableName.DEAL_INSIGHTS_DELIVERY_BY_DOMAIN_BUNDLE]: dealInsights.data.delivery_by_domain_bundle?.total_rows,
        [TableName.DEAL_INSIGHTS_AUDIENCES]: dealInsights.data.audiences?.total_rows,
        [TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_SIZE]: dealInsights.data.delivery_by_ad_size?.total_rows,
        [TableName.DEAL_INSIGHTS_DELIVERY_BY_AD_DURATION]: dealInsights.data.delivery_by_ad_duration?.total_rows,
      };

      const definedTableCounts = Object.fromEntries(
        Object.entries(tableCounts).filter(([_, value]) => value !== undefined)
      );

      updateTotalCount(definedTableCounts);
    }
  }, [dealInsights.data, updateTotalCount]);

  const contextValue = {
    filters,
    dateRange,
    setDateRange,
    setFilters,
    dealInsights,
    criteria,
    handleCriteriaChange,
    geolocation: {
      country,
      setCountry,
      metric,
      setMetric,
    },
    activeTab,
    chartDateResolutions,
    updateChartDateResolution,
  };

  return <DealInsightsContext.Provider value={contextValue}>{children}</DealInsightsContext.Provider>;
};

export const useDealInsightsContext = () => {
  const context = useContext(DealInsightsContext);

  if (!context) {
    throw new Error('useDealInsightsContext must be used within a DealInsightsProvider');
  }

  return context;
};
